import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { formatDateTime } from "../lib/constants";

/**
 * ClientNotifications — DB-backed in-app notifications list.
 * Click to mark-read; individual items link through to their project when present.
 */
export default function ClientNotifications() {
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (_e) {
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch (_e) {}
  };

  const markAll = async () => {
    if (!items) return;
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (_e) { /* notification polling is best-effort */ }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-10 py-12 fade-up" data-testid="client-notifications">
      <div className="flex items-end justify-between mb-8 gap-4">
        <div>
          <div className="label text-[color:var(--atech-copper)] mb-2">Updates</div>
          <h1 className="font-display text-4xl tracking-tight text-[color:var(--atech-text)]">Notifications</h1>
        </div>
        {items && items.some((n) => !n.read) && (
          <button
            onClick={markAll}
            className="text-xs text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] inline-flex items-center gap-2"
            data-testid="mark-all-read"
          >
            <CheckCircle2 size={14} /> Mark all read
          </button>
        )}
      </div>

      {items === null ? (
        <div className="label">Loading…</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-14 text-center">
          <Bell size={28} className="mx-auto text-[color:var(--atech-text-muted)]" />
          <div className="text-sm text-[color:var(--atech-text)] mt-4">
            You're all caught up.
          </div>
          <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 max-w-md mx-auto leading-relaxed">
            New activity on your projects will appear here.
          </p>
        </div>
      ) : (
        <div className="card-surface overflow-hidden divide-y divide-[color:var(--atech-border)]">
          {items.map((n) => (
            <div
              key={n.id}
              className={`p-5 flex items-start gap-4 hover:bg-[color:var(--atech-surface-hover)]/40 transition-colors ${!n.read ? "bg-[color:var(--atech-surface-hover)]/30" : ""}`}
              data-testid={`notification-${n.id}`}
            >
              <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-transparent border border-[color:var(--atech-text-muted)]" : "bg-[color:var(--atech-copper)]"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[color:var(--atech-text)]">
                  {n.project_id ? (
                    <Link
                      to={`/portal/projects/${n.project_id}`}
                      onClick={() => !n.read && markRead(n.id)}
                      className="hover:text-[color:var(--atech-copper)]"
                    >
                      {n.message}
                    </Link>
                  ) : (
                    n.message
                  )}
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)] mt-1">
                  <span className="font-mono">{n.event_type}</span> · {formatDateTime(n.created_at)}
                </div>
              </div>
              {!n.read && (
                <button
                  onClick={() => markRead(n.id)}
                  className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)]"
                  data-testid={`mark-read-${n.id}`}
                >
                  Mark read
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
